import { createRegisterableComponent } from '@news-mono/web-common'
import React from 'react'
import { Skeleton } from '../../../content/Skeleton/Skeleton'
import { Omny, OmnyProps } from '../../../publication/Embed/web/Omny'

export interface OmnyRouteProps extends Omit<OmnyProps, 'link'> {
    link?: string
    rssFeedUrl?: string
}

const OMNY_HOSTS = new Set(['www.omnycontent.com', 'omnycontent.com'])

const isValidOmnyRssFeedUrl = (value: string) => {
    try {
        const url = new URL(value)

        return (
            (url.protocol === 'https:' || url.protocol === 'http:') &&
            OMNY_HOSTS.has(url.hostname) &&
            url.pathname.endsWith('.rss')
        )
    } catch {
        return false
    }
}

const getLatestOmnyPlayerUrl = async (
    rssFeedUrl: string,
    signal: AbortSignal,
) => {
    if (!isValidOmnyRssFeedUrl(rssFeedUrl)) {
        return undefined
    }

    const response = await fetch(rssFeedUrl, { signal })

    if (!response.ok) {
        throw new Error(`Failed to fetch OMNY RSS: ${response.status}`)
    }

    const xml = await response.text()
    const document = new DOMParser().parseFromString(xml, 'application/xml')
    const items = Array.from(document.getElementsByTagName('item'))

    const latestItem = items.reduce<Element | undefined>((latest, item) => {
        if (!latest) {
            return item
        }

        const latestDate = Date.parse(
            latest.getElementsByTagName('pubDate')[0]?.textContent ?? '',
        )
        const itemDate = Date.parse(
            item.getElementsByTagName('pubDate')[0]?.textContent ?? '',
        )

        if (Number.isNaN(latestDate)) {
            return item
        }

        if (Number.isNaN(itemDate)) {
            return latest
        }

        return itemDate > latestDate ? item : latest
    }, undefined)

    if (!latestItem) {
        return undefined
    }

    return Array.from(latestItem.getElementsByTagName('media:content'))
        .map((content) =>
            content.getElementsByTagName('media:player')[0]?.getAttribute('url'),
        )
        .find((link): link is string => Boolean(link))
}

export const OmnyRegistration = createRegisterableComponent(
    'omny-web',
    (props: OmnyRouteProps) => {
        const [resolvedLink, setResolvedLink] = React.useState(props.link)
        const [loadingLatestLink, setLoadingLatestLink] = React.useState(
            !props.link && Boolean(props.rssFeedUrl),
        )

        React.useEffect(() => {
            setResolvedLink(props.link)
            setLoadingLatestLink(!props.link && Boolean(props.rssFeedUrl))
        }, [props.link, props.rssFeedUrl])

        React.useEffect(() => {
            if (props.link || !props.rssFeedUrl) {
                return
            }

            const rssFeedUrl = props.rssFeedUrl
            const abortController = new AbortController()

            const loadLatestEpisode = async () => {
                try {
                    const latestLink = await getLatestOmnyPlayerUrl(
                        rssFeedUrl,
                        abortController.signal,
                    )

                    setResolvedLink(latestLink)
                } catch {
                    if (!abortController.signal.aborted) {
                        setResolvedLink(undefined)
                    }
                } finally {
                    if (!abortController.signal.aborted) {
                        setLoadingLatestLink(false)
                    }
                }
            }

            void loadLatestEpisode()

            return () => {
                abortController.abort()
            }
        }, [props.link, props.rssFeedUrl])

        if (!resolvedLink) {
            return loadingLatestLink ? (
                <Skeleton count={1} height={props.height ?? 180} />
            ) : null
        }

        return (
            <Omny
                height={props.height}
                link={resolvedLink}
                width={props.width}
                verticalSpacing={props.verticalSpacing}
                smallPlaylist={props.smallPlaylist}
            />
        )
    },
)
